/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import models.*
import kotlinx.datetime.*
import kotlin.math.roundToInt

/**
 * Analytics calculator for savings goal progress
 * Leverages existing BudgetAnalytics and forecasting infrastructure
 */
class GoalProgressAnalytics {

    /**
     * Calculate comprehensive progress for a savings goal
     */
    fun calculateGoalProgress(
        goal: SavingsGoal,
        contributions: List<repository.GoalContribution>,
        transactions: List<Transaction>
    ): GoalProgress {
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())

        // Calculate current progress
        val currentSavings = goal.currentAmount
        val percentageComplete = if (goal.targetAmount > 0) {
            (currentSavings / goal.targetAmount * 100).coerceIn(0.0, 100.0)
        } else 0.0

        // Calculate monthly average contribution
        val monthlyAverage = calculateMonthlyAverageContribution(contributions)

        // Project completion date based on current trajectory
        val projectedCompletionDate = projectCompletionDate(
            goal = goal,
            monthlyAverage = monthlyAverage,
            today = today
        )

        // Determine if on track
        val isOnTrack = isGoalOnTrack(
            goal = goal,
            currentSavings = currentSavings,
            monthlyAverage = monthlyAverage,
            today = today
        )

        // Calculate days remaining
        val daysRemaining = calculateDaysRemaining(goal.deadline, today)

        // Calculate recommended monthly contribution
        val recommendedMonthly = calculateRecommendedContribution(
            goal = goal,
            currentSavings = currentSavings,
            today = today
        )

        return GoalProgress(
            goalId = goal.id,
            currentSavings = currentSavings,
            percentageComplete = percentageComplete,
            projectedCompletionDate = projectedCompletionDate,
            monthlyAverageContribution = monthlyAverage,
            isOnTrack = isOnTrack,
            daysRemaining = daysRemaining,
            recommendedMonthlyContribution = recommendedMonthly
        )
    }

    /**
     * Calculate savings potential based on spending patterns
     */
    fun calculateSavingsPotential(
        transactions: List<Transaction>,
        userId: String
    ): SavingsPotential {
        val budgetSummary = BudgetAnalytics.calculateBudgetSummary(transactions, "Current Month")

        // Identify discretionary spending
        val discretionarySpending = budgetSummary.categorySpending
            .filter { isDiscretionaryCategory(it.category) }
            .sumOf { it.amount }

        // Calculate potential savings from 20% reduction in discretionary spending
        val conservativeSavings = discretionarySpending * 0.2
        val moderateSavings = discretionarySpending * 0.35
        val aggressiveSavings = discretionarySpending * 0.5

        // Calculate current savings rate
        val currentSavingsRate = budgetSummary.netAmount / budgetSummary.totalIncome

        return SavingsPotential(
            totalIncome = budgetSummary.totalIncome,
            totalExpenses = budgetSummary.totalExpenses,
            discretionarySpending = discretionarySpending,
            currentSavingsAmount = budgetSummary.netAmount,
            currentSavingsRate = currentSavingsRate,
            conservativeSavings = conservativeSavings,
            moderateSavings = moderateSavings,
            aggressiveSavings = aggressiveSavings,
            topDiscretionaryCategories = budgetSummary.categorySpending
                .filter { isDiscretionaryCategory(it.category) }
                .sortedByDescending { it.amount }
                .take(5)
        )
    }

    /**
     * Generate progress insights and recommendations
     */
    fun generateProgressInsights(
        goal: SavingsGoal,
        progress: GoalProgress,
        savingsPotential: SavingsPotential
    ): List<ProgressInsight> {
        val insights = mutableListOf<ProgressInsight>()

        // Progress status insight
        when {
            progress.percentageComplete >= 100 -> {
                insights.add(
                    ProgressInsight(
                        type = InsightType.SUCCESS,
                        title = "Goal Achieved! 🎉",
                        message = "Congratulations! You've reached your ${goal.name} goal of $${goal.targetAmount}.",
                        priority = 1
                    )
                )
            }
            progress.percentageComplete >= 75 -> {
                insights.add(
                    ProgressInsight(
                        type = InsightType.POSITIVE,
                        title = "Almost There!",
                        message = "You're ${progress.percentageComplete.roundToInt()}% of the way to your ${goal.name} goal. Keep it up!",
                        priority = 1
                    )
                )
            }
            !progress.isOnTrack && progress.daysRemaining < 60 -> {
                val shortfall = goal.targetAmount - progress.currentSavings
                insights.add(
                    ProgressInsight(
                        type = InsightType.WARNING,
                        title = "Action Needed",
                        message = "You need to save $${(shortfall * 100).roundToInt() / 100.0} more in ${progress.daysRemaining} days. " +
                                 "Consider increasing monthly contributions to $${progress.recommendedMonthlyContribution.roundToInt()}.",
                        priority = 1
                    )
                )
            }
        }

        // Contribution pattern insight
        if (progress.monthlyAverageContribution < progress.recommendedMonthlyContribution) {
            val deficit = progress.recommendedMonthlyContribution - progress.monthlyAverageContribution
            insights.add(
                ProgressInsight(
                    type = InsightType.TIP,
                    title = "Increase Contributions",
                    message = "Your current monthly average is $${progress.monthlyAverageContribution.roundToInt()}. " +
                             "Increasing by $${deficit.roundToInt()} would get you on track.",
                    priority = 2
                )
            )
        }

        // Savings potential insight
        if (savingsPotential.moderateSavings >= progress.recommendedMonthlyContribution) {
            insights.add(
                ProgressInsight(
                    type = InsightType.OPPORTUNITY,
                    title = "Savings Opportunity Found",
                    message = "You could reach your goal by reducing discretionary spending by " +
                             "${(savingsPotential.moderateSavings / savingsPotential.discretionarySpending * 100).roundToInt()}%. " +
                             "Check the Recommendations tab for specific suggestions.",
                    priority = 2
                )
            )
        }

        // Timeline insight
        progress.projectedCompletionDate?.let { projected ->
            val comparison = projected.compareTo(goal.deadline)
            when {
                comparison < 0 -> {
                    insights.add(
                        ProgressInsight(
                            type = InsightType.POSITIVE,
                            title = "Ahead of Schedule",
                            message = "At your current rate, you'll reach your goal by $projected, ahead of your ${goal.deadline} deadline!",
                            priority = 3
                        )
                    )
                }
                comparison > 0 -> {
                    val extraDays = projected.toEpochDays() - goal.deadline.toEpochDays()
                    insights.add(
                        ProgressInsight(
                            type = InsightType.WARNING,
                            title = "Behind Schedule",
                            message = "Current trajectory suggests completion by $projected, ${extraDays} days after your deadline.",
                            priority = 1
                        )
                    )
                }
            }
        }

        return insights.sortedBy { it.priority }
    }

    /**
     * Generate quick insights without requiring full contribution history
     * Useful for UI that just needs basic observations based on goal state
     */
    fun generateQuickInsights(goal: SavingsGoal): List<ProgressInsight> {
        val insights = mutableListOf<ProgressInsight>()
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())

        val percentComplete = goalProgressPercent(goal)

        val daysRemaining = (goal.deadline.toEpochDays() - today.toEpochDays()).toInt()
        val monthsRemaining = (daysRemaining / 30).coerceAtLeast(1)
        val remaining = goal.targetAmount - goal.currentAmount
        val requiredMonthly = if (monthsRemaining > 0 && remaining > 0) remaining / monthsRemaining else 0.0

        // Progress status insights
        when {
            percentComplete >= 100 -> {
                insights.add(ProgressInsight(
                    type = InsightType.SUCCESS,
                    title = "Goal Achieved!",
                    message = "Congratulations! You've reached your ${goal.name} goal.",
                    priority = 1
                ))
            }
            percentComplete >= 75 -> {
                insights.add(ProgressInsight(
                    type = InsightType.POSITIVE,
                    title = "Almost There!",
                    message = "You're ${percentComplete.roundToInt()}% of the way. Keep going!",
                    priority = 1
                ))
            }
            percentComplete >= 50 -> {
                insights.add(ProgressInsight(
                    type = InsightType.POSITIVE,
                    title = "Halfway There",
                    message = "You've saved half of your goal. Great progress!",
                    priority = 2
                ))
            }
            percentComplete >= 25 -> {
                insights.add(ProgressInsight(
                    type = InsightType.POSITIVE,
                    title = "Good Start",
                    message = "You're ${percentComplete.roundToInt()}% toward your goal.",
                    priority = 3
                ))
            }
        }

        // Time-based insights
        when {
            daysRemaining <= 0 -> {
                insights.add(ProgressInsight(
                    type = InsightType.WARNING,
                    title = "Deadline Passed",
                    message = "Your target date has passed. Consider adjusting your deadline.",
                    priority = 1
                ))
            }
            daysRemaining < 30 && percentComplete < 100 -> {
                insights.add(ProgressInsight(
                    type = InsightType.WARNING,
                    title = "Deadline Approaching",
                    message = "Less than a month left. You need $${remaining.roundToInt()} more.",
                    priority = 1
                ))
            }
            daysRemaining < 90 && percentComplete < 50 -> {
                insights.add(ProgressInsight(
                    type = InsightType.WARNING,
                    title = "Behind Schedule",
                    message = "Only ${monthsRemaining} months left and you're at ${percentComplete.roundToInt()}%.",
                    priority = 2
                ))
            }
        }

        // Monthly contribution insight (only if goal not complete)
        if (remaining > 0 && monthsRemaining > 0) {
            insights.add(ProgressInsight(
                type = InsightType.TIP,
                title = "Monthly Target",
                message = "Save $${requiredMonthly.roundToInt()}/month to reach your goal on time.",
                priority = 3
            ))
        }

        // Completion time estimate
        if (percentComplete < 100 && monthsRemaining > 0) {
            insights.add(ProgressInsight(
                type = InsightType.TIP,
                title = "Time Remaining",
                message = "At current rate, approximately $monthsRemaining months to go.",
                priority = 4
            ))
        }

        return insights.sortedBy { it.priority }.take(3) // Limit to top 3 insights
    }

    // ========================================================================
    // PRIVATE HELPER FUNCTIONS
    // ========================================================================

    private fun calculateMonthlyAverageContribution(contributions: List<repository.GoalContribution>): Double {
        if (contributions.isEmpty()) return 0.0

        val totalContributions = contributions.sumOf { it.amount }
        val firstContribution = contributions.minByOrNull { it.date }?.date ?: return 0.0
        val lastContribution = contributions.maxByOrNull { it.date }?.date ?: return 0.0

        val monthsDiff = calculateMonthsBetween(firstContribution, lastContribution).coerceAtLeast(1)
        return totalContributions / monthsDiff
    }

    private fun projectCompletionDate(
        goal: SavingsGoal,
        monthlyAverage: Double,
        today: LocalDate
    ): LocalDate? {
        if (monthlyAverage <= 0) return null

        val remaining = goal.targetAmount - goal.currentAmount
        if (remaining <= 0) return today

        val monthsNeeded = (remaining / monthlyAverage).toInt() + 1
        return today.plus(monthsNeeded, DateTimeUnit.MONTH)
    }

    private fun isGoalOnTrack(
        goal: SavingsGoal,
        currentSavings: Double,
        monthlyAverage: Double,
        today: LocalDate
    ): Boolean {
        val monthsRemaining = calculateMonthsBetween(today, goal.deadline).coerceAtLeast(1)
        val requiredMonthly = (goal.targetAmount - currentSavings) / monthsRemaining

        return monthlyAverage >= requiredMonthly * 0.9 // 10% tolerance
    }

    private fun calculateDaysRemaining(deadline: LocalDate, today: LocalDate): Int {
        return (deadline.toEpochDays() - today.toEpochDays()).toInt().coerceAtLeast(0)
    }

    private fun calculateRecommendedContribution(
        goal: SavingsGoal,
        currentSavings: Double,
        today: LocalDate
    ): Double {
        val remaining = goal.targetAmount - currentSavings
        if (remaining <= 0) return 0.0

        val monthsRemaining = calculateMonthsBetween(today, goal.deadline).coerceAtLeast(1)
        return remaining / monthsRemaining
    }

    private fun calculateMonthsBetween(start: LocalDate, end: LocalDate): Int {
        val period = start.periodUntil(end)
        return period.years * 12 + period.months
    }

    private fun isDiscretionaryCategory(category: String): Boolean {
        val discretionaryKeywords = listOf(
            "entertainment", "dining", "shopping", "travel", "hobbies",
            "food & drink", "recreation", "general merchandise", "restaurant"
        )
        return discretionaryKeywords.any { category.contains(it, ignoreCase = true) }
    }
}

// ============================================================================
// Goal pace — an honest "On Track / Behind" derived from the goal's own fields
// (created date, current, target, deadline), so cards don't need contribution
// history. "Behind" = you've saved less than [paceTolerance] of what a steady
// plan from the goal's start to its deadline expects by now, so a goal with
// no contributions drifts into "Behind" as time passes. Also returns the $/mo
// still required to reach the target by the deadline.
//
// The plan STARTS at the earlier of the goal's creation and its first
// contribution. currentAmount counts every assigned transaction, and people
// routinely create a goal and then tag the transfers they had already been
// making — Eric's mortgage goal was created in March 2026 with contributions
// back to December 2025. Running the clock from the creation date while
// counting that money compares 8 months of saving against 5 months of plan,
// and every such goal reads as ahead of schedule. Dropping the early money
// instead would be worse: it is really in the pot.
// ============================================================================

enum class GoalPaceStatus { ACHIEVED, AHEAD, ON_TRACK, BEHIND }

data class GoalPace(
    val status: GoalPaceStatus,
    val requiredMonthly: Double,
)

// Pace vs the steady plan: below 85% of expected → Behind; above 125% → Ahead;
// in between → On Track.
private const val BEHIND_TOLERANCE = 0.85
private const val AHEAD_TOLERANCE = 1.25

/**
 * THE definition of "months remaining" on a goal: WHOLE calendar months from today to
 * the deadline.
 *
 * Whole, not started: from 3 Aug 2026 the 66th month lands 3 Feb 2032 and the 67th
 * lands 3 Mar 2032, which is past a 1 Mar deadline — so 66 contributions fit, not 67.
 * Counting the stub as a full month understates what the goal needs each month.
 *
 * Every surface must use this. Three other formulas had grown up beside it — the web
 * goals card counted calendar boundaries `(dy-cy)*12 + (dm-cm)`, and the web dashboard
 * and its recommendation used `daysRemaining / 30` — and they disagreed on screen:
 * iOS said $417/mo for the same goal the web card called $411/mo.
 */
/**
 * How funded a goal is, as a percentage. THE definition.
 *
 * Returns the TRUE value, so an over-funded goal reads 118% rather than being silently
 * capped — that is information, not an error. Progress BARS must clamp their own width
 * with [goalProgressBarPercent]; a bar cannot be 118% wide.
 *
 * There were five copies of this and two behaviours: web clamped at 100, iOS and Android
 * did not. Same shape as the four "months remaining" formulas that had the same goal
 * costing $417/mo on iOS and $411/mo on web.
 */
fun goalProgressPercent(currentAmount: Double, targetAmount: Double): Double =
    if (targetAmount > 0.0) currentAmount / targetAmount * 100.0 else 0.0

/** [goalProgressPercent] for a whole goal. Callers holding only the two numbers —
 *  the Wiz 3R card builder works off a GoalLike — use the two-argument form. */
fun goalProgressPercent(goal: SavingsGoal): Double =
    goalProgressPercent(goal.currentAmount, goal.targetAmount)

/** [goalProgressPercent] clamped to 0..100 — the width a progress bar may be drawn at. */
fun goalProgressBarPercent(goal: SavingsGoal): Double =
    goalProgressPercent(goal).coerceIn(0.0, 100.0)

fun goalMonthsRemaining(goal: SavingsGoal): Int =
    goalMonthsBetween(Clock.System.todayIn(TimeZone.currentSystemDefault()), goal.deadline)

fun computeGoalPace(goal: SavingsGoal): GoalPace {
    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val remaining = goal.targetAmount - goal.currentAmount
    if (goal.targetAmount <= 0.0 || remaining <= 0.0) return GoalPace(GoalPaceStatus.ACHIEVED, 0.0)

    val monthsRemaining = goalMonthsRemaining(goal)
    val requiredMonthly = if (monthsRemaining > 0) remaining / monthsRemaining else remaining

    val createdDate = goal.createdDate.toLocalDateTime(TimeZone.currentSystemDefault()).date
    val firstContribution = goal.firstContributionDate
        ?.let { runCatching { LocalDate.parse(it) }.getOrNull() }
    val startDate = firstContribution?.takeIf { it < createdDate } ?: createdDate
    val totalMonths = goalMonthsBetween(startDate, goal.deadline)
    val elapsedMonths = goalMonthsBetween(startDate, today)
    // Too little elapsed time (or unusable dates) to judge pace → assume on track.
    if (totalMonths <= 0 || elapsedMonths <= 0) return GoalPace(GoalPaceStatus.ON_TRACK, requiredMonthly)

    val expectedFraction = (elapsedMonths.toDouble() / totalMonths.toDouble()).coerceIn(0.0, 1.0)
    val actualFraction = goal.currentAmount / goal.targetAmount
    val ratio = if (expectedFraction > 0.0) actualFraction / expectedFraction else Double.POSITIVE_INFINITY
    val status = when {
        ratio < BEHIND_TOLERANCE -> GoalPaceStatus.BEHIND
        ratio > AHEAD_TOLERANCE -> GoalPaceStatus.AHEAD
        else -> GoalPaceStatus.ON_TRACK
    }
    return GoalPace(status, requiredMonthly)
}

/**
 * WHOLE months from [start] to [end] — the pure form of [goalMonthsRemaining], public
 * so it can be tested against fixed dates instead of whatever today happens to be.
 *
 * 2026-08-03 → 2032-03-01 is 66: the 67th month would end 2032-03-03, past the
 * deadline. Zero when [end] is not after [start].
 */
fun goalMonthsBetween(start: LocalDate, end: LocalDate): Int {
    if (end <= start) return 0
    val p = start.periodUntil(end)
    return p.years * 12 + p.months
}

/**
 * Savings potential analysis
 */
data class SavingsPotential(
    val totalIncome: Double,
    val totalExpenses: Double,
    val discretionarySpending: Double,
    val currentSavingsAmount: Double,
    val currentSavingsRate: Double,
    val conservativeSavings: Double,  // 20% reduction
    val moderateSavings: Double,      // 35% reduction
    val aggressiveSavings: Double,    // 50% reduction
    val topDiscretionaryCategories: List<CategorySpending>
)

/**
 * Progress insight for user guidance
 */
data class ProgressInsight(
    val type: InsightType,
    val title: String,
    val message: String,
    val priority: Int  // 1 = highest priority
)

/**
 * Types of insights
 */
enum class InsightType {
    SUCCESS,      // Goal achieved or major milestone
    POSITIVE,     // Good progress
    WARNING,      // Attention needed
    TIP,          // Helpful suggestion
    OPPORTUNITY   // Potential improvement
}
